import { create } from 'zustand';

/**
 * Whether there is a session.
 *
 * `unknown` is the state a tab starts in and it is not the same as `anonymous`: the refresh token
 * is an HttpOnly cookie, so on the first paint the workspace genuinely does not know yet, and a
 * shell that treated "do not know" as "signed out" would bounce every reload through the sign-in
 * page.
 */
export type SessionStatus = 'unknown' | 'anonymous' | 'authenticated';

export interface SessionState {
  readonly status: SessionStatus;
  /** When the access token stops being accepted, in epoch milliseconds. */
  readonly accessTokenExpiresAt: number | undefined;
  /** Whether this agent has a device the platform can reach them on. */
  readonly deviceReady: boolean;
  signedIn(expiresAt: number): void;
  signedOut(): void;
  setDeviceReady(ready: boolean): void;
}

/**
 * The session, as the interface needs to know about it.
 *
 * **The access token is deliberately not in here.** It lives in `src/api/auth-tokens.ts`, in a
 * module variable, for two reasons that both matter: this store is meant to be serialisable so an
 * agent can attach a redacted snapshot of it to a bug report (state management §2), and a token in
 * a serialisable store is a credential in a support ticket. Nothing here is in `localStorage`
 * either — a token that survives a tab is a token a script can read, and the refresh cookie is how
 * a reload recovers instead.
 *
 * What the user *is* — their name, permissions and scopes — is not here either. That is server
 * state and belongs in the query cache, keyed by `/v1/me`, so one copy goes stale rather than two.
 */
export const useSessionStore = create<SessionState>()((set) => ({
  status: 'unknown',
  accessTokenExpiresAt: undefined,
  deviceReady: false,
  signedIn: (expiresAt) => set({ status: 'authenticated', accessTokenExpiresAt: expiresAt }),
  signedOut: () =>
    set({
      status: 'anonymous',
      accessTokenExpiresAt: undefined,
      // A session that has ended has no device the platform can reach.
      deviceReady: false,
    }),
  setDeviceReady: (ready) => set({ deviceReady: ready }),
}));

/** The status on its own, which is what the shell re-renders on. */
export function useSessionStatus(): SessionStatus {
  return useSessionStore((state) => state.status);
}
